import React, { useEffect, useState } from 'react';
import { PageContainer } from '../components/layout/page';
import { Table } from '../components/ui/tables';
import { HeroImage, SizedImage } from '../components/ui/file-contents';
import { getShortString } from '../../common/utils';
import { SocialLink } from '../components/functional/hyper-links';
import { LeaderboardRepository } from '../../api/repositories/stats/leaderboard';
import { LeaderboardPlayer } from '../../api/repositories/stats/models/leaderboard';
import { localize } from '../../common/services/localization';
import { SubscribeGlobalState } from '../../common/hooks/global-state';

export const Leaderboard: React.FC = () => {
    const [response, setResponse] = useState<ApiDataResponse<LeaderboardPlayer[]> | undefined>();

    useEffect(() => {
        const repository = new LeaderboardRepository();
        repository.get().then(v => setResponse(v));
    }, []);

    return <PageContainer
        staticBg={true}
        containerClassName='w-[1033px] flex flex-col justify-center items-center'
        headerImgPath='/v2/images/bg/towers.png'
        child={
            <div className='max-w-[350px] sm:max-w-[650px] md:max-w-[768px] lg:max-w-[1033px]'>
                <div className='mb-[20px] lg:mb-[60px]'>
                    <h2 className="mb-4">{localize('leaderboard_title')}</h2>
                    <h3 className="">{localize('leaderboard_season')}</h3>
                </div>
                <LeaderboardTable response={response} />
            </div>
        }
    />
}

const LeaderboardTable: React.FC<{
    response: ApiDataResponse<LeaderboardPlayer[]> | undefined
}> = ({ response }) => {
    return (
        <div>
            <Table
                tableClassName='overflow-x-auto'
                bodyClassName=''
                headClassName='text-[8px] md:text-[16px] lg:text-[20px]'
                needSeparate={true}
                cols={[
                    { headLocalName: "leaderboard_table_place", className: "xl:w-[109px] text-center" },
                    { headLocalName: "leaderboard_table_player", className: "xl:w-[280px] text-left" },
                    { headLocalName: "leaderboard_table_rating", className: "xl:w-[128px] text-center" },
                    { headLocalName: "leaderboard_table_games_count", className: "xl:w-[157px] text-center" },
                    { headLocalName: "leaderboard_table_favorite_hero", className: "xl:w-[177px] text-center" },
                    { headLocalName: "leaderboard_table_media", className: "xl:w-[145px] text-center" },
                ]}
                rows={
                    response
                        ? response.data
                            ? response.data.map((player) => ({
                                href: `/players/${player.playerId}`,
                                cols: [
                                    { value: player.place, className: 'w-[109px] text-center shadow-text-gold' },
                                    {
                                        value: <div className='flex items-center gap-[10px]'>
                                            <SizedImage className='hidden xl:block w-[40px] h-[40px]' src={player.avatarUrl} />
                                            <span className='sm:hidden'>{getShortString(player.nickname, 5)}</span>
                                            <span className='hidden sm:block'>{getShortString(player.nickname, 16)}</span>
                                        </div>, className: 'w-[320px] text-start'
                                    },
                                    { value: player.rating, className: 'w-[128px] text-center shadow-text-gold' },
                                    { value: player.matchCount, className: 'w-[157px] text-center shadow-text-gold' },
                                    {
                                        value: <div className='flex justify-center items-center gap-[10px]'>
                                            <HeroImage className='shadow-gold-sm' size='80x47' heroName={player.favoriteHero} />
                                        </div>, className: 'w-[157px]'
                                    },
                                    {
                                        disableHref: true,
                                        value: <div className='flex flex-col lg:flex-row items-center justify-center gap-[10px]'>
                                            <SocialLink type='twitch' onlyLive={true} isLive={player.social?.isTwitchLive} href={player.social?.twitch} />
                                            <SocialLink type='youtube' isLive={player.social?.isYoutubeLive} href={player.social?.youtube} />
                                        </div>, className: 'w-[145px] text-center'
                                    },
                                ],
                            }))
                            : LeaderboardEmptyRows()
                        : LeaderboardEmptyRows()
                }
            />
        </div>
    );
};

const LeaderboardEmptyRows = () => {
    const retval = [];

    for (let index = 0; index < 100; index++) {
        retval.push({
            cols: [
                { value: '', className: 'h-[68px] w-[109px] text-center shadow-text-gold' },
                { value: '', },
                { value: '', className: 'w-[128px] text-center shadow-text-gold' },
                { value: '', className: 'w-[157px] text-center shadow-text-gold' },
                { value: '', className: 'w-[157px] text-center shadow-text-gold' },
                { value: '', className: 'w-[145px] text-center' },
            ],
        });
    }

    return retval;
}
